/**
 * O que o canal permite, lido do `capabilities` da conversa (H05, §8.2).
 *
 * ── Por que interpretar em vez de tipar no contrato ───────────────────────
 *
 * O `ConversaCompostaSchema` declara `capabilities` como registro sem forma, e
 * o cabeçalho dele diz por quê: *"estrutura evolui com a matriz da A03, não com
 * este contrato"*. Canal novo, provider novo ou limite novo entram sem quebrar
 * o `/v1` — o preço é que quem consome precisa ler com cuidado.
 *
 * Este arquivo é esse cuidado, num lugar só. Cada leitura tem um padrão, e o
 * padrão é sempre o mais CONSERVADOR: campo ausente vira "não sei", e "não sei"
 * nunca vira permissão. Ler direto no componente produziria o oposto —
 * `capabilities?.limites?.caracteres?.recomendado` é `undefined`, `undefined`
 * não bate em nenhuma comparação, e o limite simplesmente não se aplica.
 *
 * ── O que a §8.2 exige, e o que ela proíbe ────────────────────────────────
 *
 * *"A interface consulta `capabilities` antes de habilitar a ação."* Habilitar
 * e deixar o servidor recusar frustra: o vendedor escreve, tenta enviar, e
 * perde o texto.
 *
 * E ação indisponível **sempre com explicação**. "Fora da janela de 24 h — use
 * um template aprovado" ensina; botão cinza sem motivo gera chamado no suporte.
 * Por isso cada recusa aqui carrega o texto, e não um código.
 */

/** Sem `capabilities`, o editor cai para o mínimo (A07 C38) — nunca para o proibido. */
import type { LimitesDeAnexo } from '@cliqcrm/contracts';

export const SEM_CAPACIDADES: Capacidades = {
  canal: null,
  podeIniciar: null,
  motivoParaNaoIniciar: null,
  janela: { existe: false, duracaoHoras: null, aplicadoPor: null, reabreCom: null },
  template: { exigidoForaDaJanela: false, catalogo: [] },
  limiteRecomendado: null,
  limiteDaEngine: null,
  unidadeDoLimite: 'caracteres',
  anexoValidadoPelaEngine: false,
  /*
   * Sem capabilities, o anexo fica INDISPONÍVEL — e não "sem limite".
   *
   * É o padrão conservador do resto do arquivo aplicado aqui: quantidade zero
   * significa que a tela não oferece o botão. Cair para "tudo liberado" faria
   * a tela prometer o que não sabe se o canal aceita, que é exatamente o que a
   * §8.2 proíbe.
   */
  limitesDeAnexo: { mimeAceitos: [], maxMbPorCategoria: {}, quantidadeMaxima: 0 },
};

export interface Template {
  readonly nome: string;
  readonly idioma: string;
  readonly categoria: string;
  /**
   * O texto aprovado, com `{{1}}` onde entram os valores.
   *
   * É ELE que o cliente recebe. Sem isto na tela, quem atende escolhia um nome
   * como `veiculo_pronto_para_retirada` e apertava enviar sem ter lido a frase
   * uma única vez.
   */
  readonly corpo: string;
  /** Quantas variáveis o corpo usa. A tela pede um valor para cada. */
  readonly variaveis: number;
}

export interface Capacidades {
  readonly canal: string | null;
  /** `null` = a matriz não disse. Não é "pode" nem "não pode". */
  readonly podeIniciar: boolean | null;
  readonly motivoParaNaoIniciar: string | null;
  readonly janela: {
    readonly existe: boolean;
    readonly duracaoHoras: number | null;
    /**
     * Quem recusa envio fora da janela.
     *
     * Decide se a tela pode CONFIAR na barreira. Com `engine`, o Chatwoot
     * recusa antes de sair e o erro volta limpo. Com `provedor`, a mensagem sai
     * e a Meta devolve erro depois — a falha chega assíncrona. Com `nenhum`,
     * ninguém barra, e a mensagem some sem explicação.
     *
     * A tela avisa mais forte justamente onde ninguém garante.
     */
    readonly aplicadoPor: 'engine' | 'provedor' | 'nenhum' | null;
    readonly reabreCom: string | null;
  };
  readonly template: {
    readonly exigidoForaDaJanela: boolean;
    /** Só os enviáveis: template reprovado na Meta não é opção, é erro adiado. */
    readonly catalogo: readonly Template[];
  };
  /** Onde começar a avisar. Ver `LIMITE_*` abaixo. */
  readonly limiteRecomendado: number | null;
  /** Onde bloquear: é o que a engine recusa de verdade. */
  readonly limiteDaEngine: number | null;
  readonly unidadeDoLimite: 'caracteres' | 'bytes';
  readonly anexoValidadoPelaEngine: boolean;
  /**
   * Os limites de anexo do canal (C27, H16), do jeito que `conferirAnexos` os
   * espera — para a tela recusar ANTES de subir.
   *
   * A conferência em si NÃO mora aqui: ela vem de `@cliqcrm/contracts`, e é a
   * mesma que o servidor roda. Reescrevê-la nesta tela criaria a segunda cópia,
   * e a divergência apareceria como "a tela aceitou e o servidor recusou" — que
   * quem está usando lê como sistema quebrado.
   */
  readonly limitesDeAnexo: LimitesDeAnexo;
}

// ── Leitura defensiva ─────────────────────────────────────────────────────

const objeto = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

const texto = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const numero = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;
const booleano = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

export function lerCapacidades(bruto: Record<string, unknown> | null | undefined): Capacidades {
  const raiz = objeto(bruto);
  if (!raiz) return SEM_CAPACIDADES;

  const iniciar = objeto(raiz['iniciarConversa']);
  const janela = objeto(raiz['janelaDeAtendimento']);
  const template = objeto(raiz['template']);
  const limites = objeto(raiz['limites']);
  const caracteres = objeto(limites?.['caracteres']);
  const anexo = objeto(limites?.['anexo']);

  const aplicadoPor = texto(janela?.['aplicadoPor']);

  return {
    canal: texto(raiz['canal']),
    podeIniciar: booleano(iniciar?.['permitido']),
    motivoParaNaoIniciar: texto(iniciar?.['motivoIndisponivel']),
    janela: {
      existe: booleano(janela?.['existe']) ?? false,
      duracaoHoras: numero(janela?.['duracaoHoras']),
      aplicadoPor:
        aplicadoPor === 'engine' || aplicadoPor === 'provedor' || aplicadoPor === 'nenhum'
          ? aplicadoPor
          : null,
      reabreCom: texto(janela?.['reabreCom']),
    },
    template: {
      exigidoForaDaJanela: booleano(template?.['exigidoForaDaJanela']) ?? false,
      catalogo: lerCatalogo(template?.['catalogo']),
    },
    limiteRecomendado: numero(caracteres?.['recomendado']),
    limiteDaEngine: numero(caracteres?.['aplicadoPelaEngine']),
    unidadeDoLimite: texto(caracteres?.['unidade']) === 'bytes' ? 'bytes' : 'caracteres',
    anexoValidadoPelaEngine: booleano(anexo?.['aplicadoPelaEngine']) ?? false,
    limitesDeAnexo: {
      /*
       * Ausente vira `null` — "o canal não tem regra de tipo" —, e não lista
       * vazia.
       *
       * A diferença decide um canal inteiro: `null` é o Telegram, que aceita
       * qualquer tipo; lista vazia é um canal que não aceita anexo nenhum. E o
       * caso de ausência é real hoje: as capabilities já projetadas no banco
       * são anteriores a este campo.
       */
      mimeAceitos: listaDeTextos(anexo?.['mimeAceitos']),
      maxMbPorCategoria: mapaDeNumeros(anexo?.['maxMbPorCategoria']),
      quantidadeMaxima: numero(anexo?.['quantidadeMaxima']),
    },
  };
}

/** Lista de textos, ou `null` quando a matriz não disse. */
function listaDeTextos(bruto: unknown): readonly string[] | null {
  if (!Array.isArray(bruto)) return null;
  const itens = bruto.filter((item): item is string => typeof item === 'string');
  return itens.length > 0 ? itens : null;
}

/** Mapa de tetos por categoria. Entrada não numérica é descartada, não zerada. */
function mapaDeNumeros(bruto: unknown): Readonly<Record<string, number>> {
  const cru = objeto(bruto);
  if (!cru) return {};
  const saida: Record<string, number> = {};
  for (const [chave, valor] of Object.entries(cru)) {
    if (typeof valor === 'number' && Number.isFinite(valor) && valor > 0) saida[chave] = valor;
  }
  return saida;
}

function lerCatalogo(bruto: unknown): readonly Template[] {
  if (!Array.isArray(bruto)) return [];

  return bruto.flatMap((item) => {
    const t = objeto(item);
    const nome = texto(t?.['nome']);
    // Template não enviável fica de fora, e não desabilitado na lista: oferecer
    // o que a Meta vai recusar é adiar o erro para depois do clique, que é o
    // oposto do que a §8.2 pede.
    if (!nome || booleano(t?.['enviavel']) !== true) return [];

    return [
      {
        nome,
        idioma: texto(t?.['idioma']) ?? '—',
        categoria: texto(t?.['categoria']) ?? '—',
        corpo: texto(t?.['corpo']) ?? '',
        variaveis: Number(t?.['variaveis'] ?? 0),
      },
    ];
  });
}

// ── A janela de atendimento ───────────────────────────────────────────────

export interface EstadoDaJanela {
  readonly existe: boolean;
  readonly aberta: boolean;
  /** Quando fecha. `null` quando não há janela ou não há mensagem de entrada. */
  readonly fechaEm: Date | null;
  readonly restanteMs: number | null;
  /**
   * `true` quando há janela mas nunca houve mensagem do cliente.
   *
   * É diferente de fechada: a janela sequer abriu. E é o caso em que enviar
   * significa INICIAR conversa, com as regras do `iniciarConversa`.
   */
  readonly nuncaAbriu: boolean;
}

/**
 * O estado da janela, a partir da última mensagem DE ENTRADA.
 *
 * É a mensagem do cliente que abre a janela — não a nossa. Contar da última
 * mensagem qualquer faria a janela "renovar" sozinha toda vez que o vendedor
 * escrevesse, e ele acharia que tem 24 h para sempre.
 */
export function estadoDaJanela(
  capacidades: Capacidades,
  ultimaEntradaEm: string | null,
  agora = Date.now(),
): EstadoDaJanela {
  if (!capacidades.janela.existe || capacidades.janela.duracaoHoras === null) {
    return { existe: false, aberta: true, fechaEm: null, restanteMs: null, nuncaAbriu: false };
  }

  if (!ultimaEntradaEm) {
    return { existe: true, aberta: false, fechaEm: null, restanteMs: null, nuncaAbriu: true };
  }

  const abertura = Date.parse(ultimaEntradaEm);
  if (Number.isNaN(abertura)) {
    // Carimbo ilegível: tratar como aberta seria liberar por defeito de dado.
    // Tratar como fechada trava quem está atendendo. O meio-termo honesto é
    // dizer que não dá para saber — `existe` sem `fechaEm`.
    return { existe: true, aberta: true, fechaEm: null, restanteMs: null, nuncaAbriu: false };
  }

  const fecha = abertura + capacidades.janela.duracaoHoras * 3_600_000;
  const restante = fecha - agora;

  return {
    existe: true,
    aberta: restante > 0,
    fechaEm: new Date(fecha),
    restanteMs: restante > 0 ? restante : 0,
    nuncaAbriu: false,
  };
}

/** "restam 3 h 12 min" — o que muda o comportamento de quem atende. */
export function tempoRestanteEmPalavras(restanteMs: number | null): string | null {
  if (restanteMs === null || restanteMs <= 0) return null;

  const minutos = Math.floor(restanteMs / 60_000);
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;

  if (horas === 0) return `${minutos} min`;
  if (resto === 0) return `${horas} h`;
  return `${horas} h ${resto} min`;
}

/** Menos de uma hora muda o tom do aviso: aí a pressa é real. */
export const JANELA_APERTADA_MS = 3_600_000;

// ── O veredito que o editor consome ───────────────────────────────────────

export interface Permissao {
  readonly podeEnviar: boolean;
  /** Sempre presente quando `podeEnviar` é falso — a §8.2 exige explicar. */
  readonly porque: string | null;
  /** `true` quando só dá para enviar escolhendo um template aprovado. */
  readonly exigeTemplate: boolean;
  /** Avisa sem impedir — o caso em que ninguém garante a barreira. */
  readonly ressalva: string | null;
}

/**
 * Pode enviar agora?
 *
 * Nota interna NÃO passa por aqui: ela não sai pelo canal, então janela,
 * template e limite de canal não a alcançam. Bloquear a nota porque a janela do
 * WhatsApp fechou impediria justamente o registro que o vendedor precisa deixar
 * quando não consegue responder.
 */
export function podeEnviarAgora(
  capacidades: Capacidades,
  janela: EstadoDaJanela,
  temTemplateEscolhido: boolean,
): Permissao {
  if (janela.nuncaAbriu) {
    if (capacidades.podeIniciar === false) {
      return {
        podeEnviar: false,
        porque:
          capacidades.motivoParaNaoIniciar ??
          'Este canal não permite iniciar a conversa — é o cliente que precisa escrever primeiro.',
        exigeTemplate: false,
        ressalva: null,
      };
    }
    if (capacidades.template.exigidoForaDaJanela && !temTemplateEscolhido) {
      return {
        podeEnviar: false,
        porque:
          'Para começar uma conversa por aqui é preciso usar uma mensagem pronta — daquelas ' +
          'aprovadas antes pelo canal.',
        exigeTemplate: true,
        ressalva: null,
      };
    }
  }

  if (janela.existe && !janela.aberta && !janela.nuncaAbriu) {
    if (capacidades.template.exigidoForaDaJanela && !temTemplateEscolhido) {
      return {
        podeEnviar: false,
        porque:
          /*
           * A frase diz O QUE FAZER, e não a regra.
           *
           * A versão anterior explicava o funcionamento do canal — "fora desse
           * prazo, este canal só entrega mensagens prontas". Quem lê está com
           * o cliente esperando e precisa do próximo passo, não da explicação:
           * a regra ele aprende na terceira vez, e o passo ele precisa agora.
           */
          `Passaram mais de ${capacidades.janela.duracaoHoras ?? 24} h desde a última mensagem ` +
          `do cliente. Para enviar uma nova mensagem, escolha uma das mensagens prontas já ` +
          `aprovadas.`,
        exigeTemplate: true,
        ressalva: null,
      };
    }
  }

  // Aberta, ou sem janela: pode enviar. A ressalva cobre o caso em que ninguém
  // aplica a regra — a mensagem sai e pode sumir no provedor sem erro visível.
  const ressalva =
    janela.existe && !janela.aberta && capacidades.janela.aplicadoPor === 'nenhum'
      ? 'O prazo de resposta livre já passou, e este canal não barra o envio: a mensagem pode ' +
        'não chegar ao cliente, e ninguém vai avisar.'
      : null;

  return { podeEnviar: true, porque: null, exigeTemplate: false, ressalva };
}

export type Excesso = 'dentro' | 'passou-do-recomendado' | 'passou-do-limite';

/**
 * Quanto do limite já foi usado — enquanto se digita, não no envio.
 *
 * A Técnica da task: *"limites aplicados enquanto digita, não no envio.
 * Descobrir no envio que passou do limite é perder o trabalho."*
 *
 * São dois limites, e a diferença importa. O `recomendado` é da matriz do canal
 * e avisa; o `aplicadoPelaEngine` é o que o Chatwoot recusa de verdade, e
 * bloqueia. Tratar os dois como um só ou impediria envio que funciona, ou
 * deixaria passar envio que falha.
 */
export function medirConteudo(
  texto: string,
  capacidades: Capacidades,
): { readonly tamanho: number; readonly excesso: Excesso; readonly limite: number | null } {
  // Bytes quando o canal conta bytes: um emoji ocupa quatro, e contar
  // caracteres deixaria a mensagem passar aqui e ser recusada lá.
  const tamanho =
    capacidades.unidadeDoLimite === 'bytes' ? new TextEncoder().encode(texto).length : texto.length;

  if (capacidades.limiteDaEngine !== null && tamanho > capacidades.limiteDaEngine) {
    return { tamanho, excesso: 'passou-do-limite', limite: capacidades.limiteDaEngine };
  }
  if (capacidades.limiteRecomendado !== null && tamanho > capacidades.limiteRecomendado) {
    return { tamanho, excesso: 'passou-do-recomendado', limite: capacidades.limiteRecomendado };
  }

  return {
    tamanho,
    excesso: 'dentro',
    limite: capacidades.limiteRecomendado ?? capacidades.limiteDaEngine,
  };
}
